import React, { useState } from "react";
import { Box, Paper, Tooltip, Typography } from "@mui/material";
import PersonOffOutlinedIcon from "@mui/icons-material/PersonOffOutlined";
import { useGetCopilotBudgetSummaryQuery, useGetEnterpriseCopilotBudgetSummaryQuery } from "state/rtkQuery/copilot/copilotQueries";
import BudgetAtRiskModal from "./BudgetAtRiskModal";

const BUDGET_RISK_ACCENT = "#E2231A";

// "Users Near Budget Limit" KPI tile + its drill-down modal — extracted out of
// StatCards so it can be rendered standalone (e.g. alongside a page's own
// custom KPI cards, like the Enterprise Usage page) without dragging in
// StatCards' other 5 org-summary tiles.
const BudgetAtRiskTile = ({ orgId, costCenterId, platformId, deloitteOverageRate, isEnterprise = false }) => {
  const [showAtRiskModal, setShowAtRiskModal] = useState(false);

  // Budget records are a running total per user (upserted, no period boundary),
  // so this count is org/cost-center-wide (or, in enterprise mode, platform-wide)
  // and date-independent — it will not change when a date-range picker changes.
  const scopedBudgetQuery = useGetCopilotBudgetSummaryQuery(
    { orgId, costCenterId, platformId },
    { skip: isEnterprise || !(orgId || costCenterId) || !platformId, refetchOnMountOrArgChange: true }
  );
  const enterpriseBudgetQuery = useGetEnterpriseCopilotBudgetSummaryQuery(
    { platformId },
    { skip: !isEnterprise || !platformId, refetchOnMountOrArgChange: true }
  );
  const { data: budgetSummary, isLoading: isBudgetLoading } = isEnterprise ? enterpriseBudgetQuery : scopedBudgetQuery;
  const usersAtRisk = budgetSummary?.users_near_or_over_budget ?? budgetSummary?.total;
  const thresholdPct = budgetSummary?.threshold_pct ?? 80;
  const canOpen = (isEnterprise || orgId || costCenterId) && platformId;

  return (
    <>
      <Tooltip
        title={`Users who have consumed ${thresholdPct}% or more of their individual budget. Excludes users with no budget set. Not affected by the date range above — budgets are a running total, not scoped to a period.`}
        placement="top"
        arrow
        componentsProps={{
          tooltip: { sx: { fontSize: "0.8rem", lineHeight: 1.5, px: 1.5, py: 1 } },
        }}
      >
        <Paper
          elevation={0}
          onClick={() => {
            if (canOpen) setShowAtRiskModal(true);
          }}
          sx={{
            borderRadius: "10px",
            border: `1px solid ${BUDGET_RISK_ACCENT}`,
            borderTop: `3px solid ${BUDGET_RISK_ACCENT}`,
            padding: "20px 24px",
            display: "flex",
            flexDirection: "column",
            gap: "10px",
            backgroundColor: "#fff",
            cursor: canOpen ? "pointer" : "default",
            transition: "box-shadow 0.2s",
            "&:hover": {
              boxShadow: "0 10px 24px rgba(0, 0, 0, 0.12)",
            },
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <PersonOffOutlinedIcon sx={{ color: BUDGET_RISK_ACCENT, fontSize: 28 }} />
            <Box>
              <Typography sx={{ fontSize: "0.8rem", color: "#666", fontWeight: 500 }}>
                Users Near Budget Limit
              </Typography>
              <Typography sx={{ fontSize: "0.65rem", color: "#aaa", lineHeight: 1.2 }}>
                {`≥${thresholdPct}% consumed · all-time`}
              </Typography>
            </Box>
          </Box>
          <Typography
            sx={{ fontSize: "1.9rem", fontWeight: 700, color: "#1a1a1a", lineHeight: 1 }}
          >
            {isBudgetLoading ? "—" : usersAtRisk ?? "—"}
          </Typography>
        </Paper>
      </Tooltip>

      <BudgetAtRiskModal
        open={showAtRiskModal}
        onClose={() => setShowAtRiskModal(false)}
        orgId={orgId}
        costCenterId={costCenterId}
        platformId={platformId}
        thresholdPct={thresholdPct}
        deloitteOverageRate={deloitteOverageRate}
        isEnterprise={isEnterprise}
      />
    </>
  );
};

export default BudgetAtRiskTile;
